import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ISavingsDepositRecord } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ITopUpBucketDepositRequest {
  debitAccountNumber: string;
  savingsAccountNumber: string;
  amount: number;
  pin?: string;
}

interface ITopUpBucketDepositResponse {
  status?: boolean;
  message?: string;
  totalCount?: number;
  data?: ISavingsDepositRecord[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<ITopUpBucketDepositResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const topUpBucketDepositAsync = createAsyncThunk(
  'savings/topUpDeposit',
  async (
    values: ITopUpBucketDepositRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ITopUpBucketDepositResponse>(
        '/savings/deposits',
        values,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ITopUpBucketDepositResponse>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const topUpBucketDepositSlice = createSlice({
  name: 'savings/topUpDeposit',
  initialState,
  reducers: {
    resetTopUpBucketDeposit(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(topUpBucketDepositAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(topUpBucketDepositAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ITopUpBucketDepositResponse;
      })
      .addCase(topUpBucketDepositAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ITopUpBucketDepositResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetTopUpBucketDeposit } = topUpBucketDepositSlice.actions;

export default topUpBucketDepositSlice.reducer;
